/**
 * Landlord Portal Main App
 * Sets up routing and authentication context
 */

import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { onReauthRequired, getReauthReturnTo, onAccountDeactivated } from './lib/api';
import { ProtectedRoute } from './components/ProtectedRoute';
import { FeatureRoute } from './components/FeatureRoute';
import { AppShell } from './components/navigation/AppShell';
import { PageTitleProvider } from './hooks/usePageTitle';
import { LandlordErrorFallback } from './components/ErrorBoundary';
import {
  AnalyticsProvider,
  AnalyticsErrorBoundary,
  CookieConsentRoot,
  useCookieConsentSync,
  useIdentifyUser,
} from '@resida/analytics';
import { cookieConsentApi } from './lib/cookie-consent.api';
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { MagicLinkVerifyPage } from './pages/auth/MagicLinkVerifyPage';
import { MagicLinkRegisterVerifyPage } from './pages/auth/MagicLinkRegisterVerifyPage';
import { EmailCodeVerifyPage } from './pages/auth/EmailCodeVerifyPage';
import { DashboardPage } from './pages/DashboardPage';
import { ChecklistsPage } from './pages/ChecklistsPage';
import { PortfoliosPage } from './pages/PortfoliosPage';
import { PortfolioDetailPage } from './pages/PortfolioDetailPage';
import { PropertiesPage } from './pages/PropertiesPage';
import { AddPropertyWizardPage } from './pages/AddPropertyWizardPage';
import { PropertyDetailPage } from './pages/PropertyDetailPage';
import { UnitsPage } from './pages/UnitsPage';
import { UnitDetailPage } from './pages/UnitDetailPage';
import { PaymentsDashboardPage } from './pages/PaymentsDashboardPage';
import { LeasesPage } from './pages/LeasesPage';
import { LeaseDetailPage } from './pages/LeaseDetailPage';
import { RentIncreasesPage } from './pages/RentIncreasesPage';
import { RentIncreaseDetailPage } from './pages/RentIncreaseDetailPage';
import { NewRentIncreasePage } from './pages/NewRentIncreasePage';
import { VerificationPage } from './pages/VerificationPage';
import { BillingPage } from './pages/BillingPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { CheckoutSuccessPage } from './pages/CheckoutSuccessPage';
import { ChatPage } from './pages/ChatPage';
import { SupportPage } from './pages/SupportPage';
import { HelpCenterPage } from './pages/HelpCenterPage';
import { HelpChatPage } from './pages/HelpChatPage';
import { HelpArticlePage } from './pages/HelpArticlePage';
import { HelpTopicPage } from './pages/HelpTopicPage';
import { HelpProvider } from './components/help/HelpProvider';
import { EmailInboxPage } from './pages/EmailInboxPage';
import { ExpenseDetailPage } from './pages/ExpenseDetailPage';
import { ExpenseDashboardPage } from './pages/ExpenseDashboardPage';
import { ExpenseReviewQueuePage } from './pages/ExpenseReviewQueuePage';
import { ExpenseAggregationDetailPage } from './pages/ExpenseAggregationDetailPage';
import { ExpenseAggregationsPage } from './pages/ExpenseAggregationsPage';
import { RecurringExpensesPage } from './pages/RecurringExpensesPage';
import { ExpenseReportPage } from './pages/ExpenseReportPage';
import { SettingsPage } from './pages/SettingsPage';
import { RepairsPage } from './pages/RepairsPage';
import { RepairDetailPage } from './pages/RepairDetailPage';
import { RepairTestHarnessPage } from './pages/RepairTestHarnessPage';
import { SettingsVendorsPage } from './pages/SettingsVendorsPage';
import { SettingsAgentsPage } from './pages/SettingsAgentsPage';
import { AgentPlaybookPage } from './pages/AgentPlaybookPage';
import { ImprovementConversationProvider } from './components/agent/improvement/ImprovementConversationProvider';
import { ImprovementConversationPage } from './components/agent/improvement/ImprovementConversationSheet';
import { OAuthConsentPage } from './pages/OAuthConsentPage';
import { ConnectedAppsPage } from './pages/ConnectedAppsPage';
import { ToolPermissionsPage } from './pages/ToolPermissionsPage';
import { SettingsProfilePage } from './pages/SettingsProfilePage';
import { SettingsAchAccountsPage } from './pages/SettingsAchAccountsPage';
import { SettingsLinkedAccountsPage } from './pages/SettingsLinkedAccountsPage';
import { SettingsLegalPage } from './pages/SettingsLegalPage';
import { CredentialsPage } from './pages/CredentialsPage';
import { AttentionPage } from './pages/AttentionPage';
import { SigningEnvelopesPage } from './pages/signing/SigningEnvelopesPage';
import { SigningEnvelopeDetailPage } from './pages/signing/SigningEnvelopeDetailPage';
import { PublicSigningPage } from './pages/signing/PublicSigningPage';
import { SigningCeremonyPage } from './pages/signing/SigningCeremonyPage';
import { LeaseWizardPage } from './pages/lease-wizard/LeaseWizardPage';
import { LeaseAmendmentPage } from './pages/LeaseAmendmentPage';
import { UnitDisclosuresPage } from './pages/UnitDisclosuresPage';
import { UnitDisclosureEditPage } from './pages/UnitDisclosureEditPage';
import { ConditionTemplatePage } from './pages/ConditionTemplatePage';
import { WalkthroughRunnerPage } from './pages/WalkthroughRunnerPage';
import { DeductionReportPage } from './pages/DeductionReportPage';
import { ExternalPaymentsHubPage } from './pages/ExternalPaymentsHubPage';
import { ReportsPage } from './pages/ReportsPage';
import { TaxReportsPage } from './pages/TaxReportsPage';
import { ExternalPaymentOnboardingPage } from './pages/ExternalPaymentOnboardingPage';
import { TransactionFeedPage } from './pages/TransactionFeedPage';
import { RelaySimulatorPage } from './pages/RelaySimulatorPage';
import { RelaySimulatorCasePage } from './pages/RelaySimulatorCasePage';
import { LeasingLayout } from './pages/leasing/LeasingLayout';
import { LeasingDashboardPage } from './pages/leasing/LeasingDashboardPage';
import { ListingsIndexPage } from './pages/leasing/ListingsIndexPage';
import { ListingDetailPage } from './pages/leasing/ListingDetailPage';
import { ListingPreviewPage } from './pages/leasing/ListingPreviewPage';
import { ListingGuidePage, GuideResumePage } from './pages/leasing/listing-guide/ListingGuidePage';
import { ListingGuideStartPage } from './pages/leasing/listing-guide/ListingGuideStartPage';
import { LeadsIndexPage } from './pages/leasing/LeadsIndexPage';
import { LeadDetailPage } from './pages/leasing/LeadDetailPage';
import { LeadInboxPage } from './pages/leasing/LeadInboxPage';
import { ApplicationsIndexPage } from './pages/leasing/ApplicationsIndexPage';
import { RelayRecipientsPage } from './pages/RelayRecipientsPage';
import { RelayRecipientDetailPage } from './pages/RelayRecipientDetailPage';
import { RelayBudgetsPage } from './pages/RelayBudgetsPage';
import { RelayPolicyBlocksPage } from './pages/RelayPolicyBlocksPage';
import { SettingsAgentEmailPage } from './pages/SettingsAgentEmailPage';
import { SettingsLayout } from './components/settings/SettingsLayout';
import { ReauthDialog } from './components/auth/ReauthDialog';
import { SupportChatProvider } from './contexts/SupportChatContext';
import { CentrifugoProvider } from './contexts/CentrifugoContext';
import { InstallPromptProvider } from './contexts/InstallPromptContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { InstallPromptBanner } from './components/InstallPromptBanner';
import { NotificationPermissionPrompt } from './components/NotificationPermissionPrompt';
import { SettingsNotificationsPage } from './pages/SettingsNotificationsPage';
import { AgentResponseQueuePage } from './pages/AgentResponseQueuePage';
import { AgentEscalationQueuePage } from './pages/AgentEscalationQueuePage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * Protected Layout Wrapper
 * Wraps protected routes with the Layout component
 */
function ProtectedLayout({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

/**
 * App Routes Component
 * Must be inside BrowserRouter for useNavigate in AuthProvider
 */
function AnalyticsIdentify() {
  const { manager } = useAuth();
  useIdentifyUser(manager?.id ?? null, 'landlord', 'landlord');
  // Signed-in cookie-consent record + sign-in reconciliation (BR-11, BR-12).
  useCookieConsentSync(manager?.id ?? null, cookieConsentApi);
  return null;
}

function AppRoutes() {
  // Dev-only render-error trigger for e2e coverage of ERR-1 (AnalyticsErrorBoundary): any
  // route visited with ?__e2eCrash=1 throws synchronously during render, letting a test force
  // an uncaught error under the boundary without needing app-specific broken state. Gated on
  // import.meta.env.DEV so `vite build` strips this branch entirely from production bundles —
  // never reachable outside a dev/e2e server.
  if (import.meta.env.DEV && new URLSearchParams(window.location.search).get('__e2eCrash') === '1') {
    throw new Error('__e2eCrash: forced render error for e2e testing');
  }

  const navigate = useNavigate();
  const [showReauthModal, setShowReauthModal] = useState(false);

  useEffect(() => {
    const unsubscribe = onReauthRequired(() => {
      setShowReauthModal(true);
    });
    return () => unsubscribe();
  }, []);

  // Redirect to billing page when any API call returns ACCOUNT_DEACTIVATED
  useEffect(() => {
    const unsubscribe = onAccountDeactivated(() => {
      // Use window.location to ensure a full navigation (not just a React route change)
      // so that any in-flight queries are abandoned
      const currentPath = window.location.pathname;
      if (!currentPath.endsWith('/settings/billing') && !currentPath.includes('/settings/billing/')) {
        window.location.assign('/landlord/settings/billing');
      }
    });
    return () => unsubscribe();
  }, []);

  // Leaving the blocked action: its request never settles, so drop the page it was started from.
  function handleReauthCancel() {
    setShowReauthModal(false);
    navigate('/dashboard');
  }

  return (
    <AuthProvider>
      <AnalyticsIdentify />
      <CentrifugoProvider>
      <SupportChatProvider>
      <InstallPromptProvider>
      <InstallPromptBanner />
      <NotificationPermissionPrompt />
      <PageTitleProvider>
      <ImprovementConversationProvider>
      <HelpProvider>
      {showReauthModal && <ReauthDialog returnTo={getReauthReturnTo() || '/dashboard'} onCancel={handleReauthCancel} />}
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/auth/magic-link/verify" element={<MagicLinkVerifyPage />} />
        <Route path="/auth/register/magic-link/verify" element={<MagicLinkRegisterVerifyPage />} />
        <Route path="/auth/email-code/verify" element={<EmailCodeVerifyPage />} />
        <Route path="/sign/:token" element={<PublicSigningPage />} />
        {/* Protected routes with Layout */}
        <Route
          path="/dashboard"
          element={
            <ProtectedLayout>
              <DashboardPage />
            </ProtectedLayout>
          }
        />

        {/* Portfolio routes */}
        <Route
          path="/portfolios"
          element={
            <ProtectedLayout>
              <PortfoliosPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/portfolios/:portfolioId"
          element={
            <ProtectedLayout>
              <PortfolioDetailPage />
            </ProtectedLayout>
          }
        />

        {/* Property routes */}
        <Route
          path="/properties"
          element={
            <ProtectedLayout>
              <PropertiesPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/properties/new"
          element={
            <ProtectedLayout>
              <AddPropertyWizardPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/properties/:propertyId"
          element={
            <ProtectedLayout>
              <PropertyDetailPage />
            </ProtectedLayout>
          }
        />

        {/* Unit routes */}
        <Route
          path="/units"
          element={
            <ProtectedLayout>
              <UnitsPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/units/new"
          element={
            <ProtectedLayout>
              <UnitsPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/units/:unitId"
          element={
            <ProtectedLayout>
              <UnitDetailPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/units/:unitId/disclosures"
          element={
            <ProtectedLayout>
              <UnitDisclosuresPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/units/:unitId/disclosures/:disclosureId"
          element={
            <ProtectedLayout>
              <UnitDisclosureEditPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/units/:unitId/create-lease"
          element={
            <ProtectedLayout>
              <LeaseWizardPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/units/:unitId/condition-template"
          element={
            <ProtectedLayout>
              <ConditionTemplatePage />
            </ProtectedLayout>
          }
        />

        {/* Lease routes */}
        <Route
          path="/leases"
          element={
            <ProtectedLayout>
              <LeasesPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/leases/:leaseId"
          element={
            <ProtectedLayout>
              <LeaseDetailPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/leases/:leaseId/amendments/new"
          element={
            <ProtectedLayout>
              <LeaseAmendmentPage />
            </ProtectedLayout>
          }
        />

        {/* Rent Increase routes (lease-lifecycle — The Rent Increase Process) */}
        <Route
          path="/rent-increases"
          element={
            <ProtectedLayout>
              <RentIncreasesPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/leases/:leaseId/rent-increases/new"
          element={
            <ProtectedLayout>
              <NewRentIncreasePage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/rent-increases/:id"
          element={
            <ProtectedLayout>
              <RentIncreaseDetailPage />
            </ProtectedLayout>
          }
        />

        {/* Condition walkthrough (JR-2/JR-4/JR-6) + deduction report (JR-5) routes */}
        <Route
          path="/leases/:leaseId/condition-walkthroughs/:phase"
          element={
            <ProtectedLayout>
              <WalkthroughRunnerPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/leases/:leaseId/deduction-report"
          element={
            <ProtectedLayout>
              <DeductionReportPage />
            </ProtectedLayout>
          }
        />

        {/* Bank Accounts — redirect to settings */}
        <Route path="/bank-accounts" element={<Navigate to="/settings/ach-accounts" replace />} />

        {/* External Payments Hub */}
        <Route
          path="/external-payments"
          element={
            <ProtectedLayout>
              <ExternalPaymentsHubPage />
            </ProtectedLayout>
          }
        />

        {/* Reports hub — Income Analytics, Expense Reports, Tax Reports */}
        <Route
          path="/reports"
          element={<ProtectedLayout><ReportsPage /></ProtectedLayout>}
        >
          <Route index element={<Navigate to="analytics" replace />} />
          <Route path="analytics" element={<PaymentsDashboardPage />} />
          <Route path="expenses" element={<ExpenseReportPage />} />
          <Route path="tax" element={<TaxReportsPage />} />
        </Route>

        {/* Backwards-compat redirects for old report URLs */}
        <Route path="/tax" element={<Navigate to="/reports/tax" replace />} />
        <Route path="/payments/analytics" element={<Navigate to="/reports/analytics" replace />} />

        {/* Backwards-compatibility redirects */}
        <Route path="/payments" element={<Navigate to="/bank-accounts" replace />} />

        {/* Verification routes */}
        <Route
          path="/verification"
          element={
            <ProtectedLayout>
              <VerificationPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/verification/complete"
          element={<Navigate to="/settings/ach-accounts?stripeReturn=true" replace />}
        />
        <Route
          path="/verification/refresh"
          element={
            <ProtectedLayout>
              <VerificationPage />
            </ProtectedLayout>
          }
        />

        {/* Billing routes — now under settings */}
        <Route path="/billing" element={<Navigate to="/settings/billing" replace />} />
        <Route path="/billing/checkout" element={<Navigate to="/settings/billing/checkout" replace />} />
        <Route path="/billing/success" element={<Navigate to="/settings/billing/success" replace />} />
        <Route
          path="/settings/billing"
          element={
            <ProtectedLayout>
              <BillingPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/settings/billing/checkout"
          element={
            <ProtectedLayout>
              <CheckoutPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/settings/billing/success"
          element={
            <ProtectedLayout>
              <CheckoutSuccessPage />
            </ProtectedLayout>
          }
        />

        {/* Chat routes */}
        <Route
          path="/chat"
          element={
            <ProtectedLayout>
              <ChatPage />
            </ProtectedLayout>
          }
        />

        {/* Support route */}
        <Route
          path="/support"
          element={
            <ProtectedLayout>
              <SupportPage />
            </ProtectedLayout>
          }
        />

        {/* Help center routes (help-center-chatbot). /help/chat and /help/topics/:category are declared before /help/:id. */}
        <Route
          path="/help"
          element={
            <ProtectedLayout>
              <HelpCenterPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/help/chat"
          element={
            <ProtectedLayout>
              <HelpChatPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/help/topics/:category"
          element={
            <ProtectedLayout>
              <HelpTopicPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/help/:id"
          element={
            <ProtectedLayout>
              <HelpArticlePage />
            </ProtectedLayout>
          }
        />

        {/* Email routes */}
        <Route
          path="/email"
          element={
            <ProtectedLayout>
              <EmailInboxPage />
            </ProtectedLayout>
          }
        />

        <Route
          path="/checklists"
          element={
            <ProtectedLayout>
              <ChecklistsPage />
            </ProtectedLayout>
          }
        />

        {/* Expense routes — dashboard and sub-sections */}
        <Route
          path="/expenses"
          element={
            <ProtectedLayout>
              <ExpenseDashboardPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/expenses/confirm"
          element={
            <ProtectedLayout>
              <ExpenseReviewQueuePage />
            </ProtectedLayout>
          }
        />
        {/* Backwards-compat redirect for old review queue path */}
        <Route path="/expenses/review" element={<Navigate to="/expenses/confirm" replace />} />
        <Route
          path="/expenses/recurring"
          element={
            <ProtectedLayout>
              <RecurringExpensesPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/expenses/aggregations"
          element={
            <ProtectedLayout>
              <ExpenseAggregationsPage />
            </ProtectedLayout>
          }
        />
        {/* Backwards-compat redirects for old standalone routes */}
        <Route path="/recurring-expenses" element={<Navigate to="/expenses/recurring" replace />} />
        <Route path="/expense-aggregations" element={<Navigate to="/expenses/aggregations" replace />} />
        <Route
          path="/expenses/:id"
          element={
            <ProtectedLayout>
              <ExpenseDetailPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/expense-aggregations/:id"
          element={
            <ProtectedLayout>
              <ExpenseAggregationDetailPage />
            </ProtectedLayout>
          }
        />
        <Route path="/expense-reports" element={<Navigate to="/reports/expenses" replace />} />

        {/* Attention Surface (v1: "Readiness Checklist") */}
        <Route
          path="/attention"
          element={
            <ProtectedLayout>
              <AttentionPage />
            </ProtectedLayout>
          }
        />
        {/* /readiness -> /attention: v1 bookmarks/links still resolve */}
        <Route path="/readiness" element={<Navigate to="/attention" replace />} />

        {/* Repair routes */}
        <Route
          path="/repairs/:id/test-harness"
          element={
            <ProtectedLayout>
              <RepairTestHarnessPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/repairs/:id"
          element={
            <ProtectedLayout>
              <RepairDetailPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/repairs"
          element={
            <ProtectedLayout>
              <RepairsPage />
            </ProtectedLayout>
          }
        />

        {/* Settings routes */}
        <Route
          path="/settings"
          element={
            <ProtectedLayout>
              <SettingsPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/settings/profile"
          element={
            <ProtectedLayout>
              <SettingsProfilePage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/settings/ach-accounts"
          element={
            <ProtectedLayout>
              <SettingsAchAccountsPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/settings/linked-accounts"
          element={
            <ProtectedLayout>
              <SettingsLinkedAccountsPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/settings/security"
          element={
            <ProtectedLayout>
              <CredentialsPage />
            </ProtectedLayout>
          }
        />

        <Route
          path="/settings/notifications"
          element={
            <ProtectedLayout>
              <SettingsNotificationsPage />
            </ProtectedLayout>
          }
        />

        <Route
          path="/settings/legal"
          element={
            <ProtectedLayout>
              <SettingsLegalPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/settings/vendors"
          element={
            <ProtectedLayout>
              <SettingsVendorsPage />
            </ProtectedLayout>
          }
        />
        {/* Agent email — the relay governance pages, nested under Settings (BR-128) */}
        <Route
          path="/settings/agent-email"
          element={
            <ProtectedLayout>
              <FeatureRoute featureKey="agentic_support" fallbackTo="/settings/security">
                <SettingsLayout>
                  <SettingsAgentEmailPage />
                </SettingsLayout>
              </FeatureRoute>
            </ProtectedLayout>
          }
        >
          <Route index element={<Navigate to="/settings/agent-email/recipients" replace />} />
          <Route path="recipients" element={<RelayRecipientsPage />} />
          <Route path="recipients/:address" element={<RelayRecipientDetailPage />} />
          <Route path="budgets" element={<RelayBudgetsPage />} />
          <Route path="policy-blocks" element={<RelayPolicyBlocksPage />} />
        </Route>
        <Route
          path="/settings/agents"
          element={
            <ProtectedLayout>
              <SettingsAgentsPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/settings/agents/:agentKind/playbook"
          element={
            <ProtectedLayout>
              <AgentPlaybookPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/settings/connected-apps"
          element={
            <ProtectedLayout>
              <ConnectedAppsPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/settings/tool-permissions"
          element={
            <ProtectedLayout>
              <ToolPermissionsPage />
            </ProtectedLayout>
          }
        />
        {/* OAuth Consent */}
        <Route
          path="/oauth/consent"
          element={
            <ProtectedLayout>
              <OAuthConsentPage />
            </ProtectedLayout>
          }
        />

        {/* Signing routes */}
        <Route
          path="/signing"
          element={
            <ProtectedLayout>
              <SigningEnvelopesPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/signing/:id"
          element={
            <ProtectedLayout>
              <SigningEnvelopeDetailPage />
            </ProtectedLayout>
          }
        />
        {/* Full-screen in-app signing (no AppShell): consent -> embedded ceremony at full width */}
        <Route
          path="/signing/:id/sign"
          element={
            <ProtectedRoute>
              <SigningCeremonyPage />
            </ProtectedRoute>
          }
        />

        {/* External Payments routes — redirect to unified Bank Accounts page */}
        <Route path="/external-payments/bank-accounts" element={<Navigate to="/bank-accounts" replace />} />
        <Route
          path="/properties/:propertyId/external-payments/onboarding"
          element={
            <ProtectedLayout>
              <ExternalPaymentOnboardingPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/properties/:propertyId/external-payments/transactions"
          element={
            <ProtectedLayout>
              <TransactionFeedPage />
            </ProtectedLayout>
          }
        />

        {/* Leasing — hub with sub-navigation (Dashboard · Inbox · Leads · Listings) */}
        <Route
          path="/leasing"
          element={
            <ProtectedLayout>
              <LeasingLayout />
            </ProtectedLayout>
          }
        >
          <Route index element={<Navigate to="/leasing/dashboard" replace />} />
          <Route path="dashboard" element={<LeasingDashboardPage />} />
          <Route path="inbox" element={<LeadInboxPage />} />
          <Route path="leads" element={<LeadsIndexPage />} />
          <Route path="applications" element={<ApplicationsIndexPage />} />
          <Route path="listings" element={<ListingsIndexPage />} />
        </Route>

        {/* Guided listing setup (BR-107): start, steps before the draft Listing exists, steps after */}
        <Route
          path="/leasing/listings/new"
          element={
            <ProtectedLayout>
              <ListingGuideStartPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/leasing/listings/new/:unitId"
          element={
            <ProtectedLayout>
              <GuideResumePage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/leasing/listings/new/:unitId/:step"
          element={
            <ProtectedLayout>
              <ListingGuidePage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/leasing/listings/:id/setup"
          element={
            <ProtectedLayout>
              <GuideResumePage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/leasing/listings/:id/setup/:step"
          element={
            <ProtectedLayout>
              <ListingGuidePage />
            </ProtectedLayout>
          }
        />
        {/* Draft preview (BR-108, BR-109): a full-page view in the prospect theme, outside the app shell */}
        <Route
          path="/leasing/listings/:id/preview"
          element={
            <ProtectedRoute>
              <ListingPreviewPage />
            </ProtectedRoute>
          }
        />

        {/* Leasing detail pages — rendered outside the hub layout (no tab strip) */}
        <Route
          path="/leasing/listings/:id"
          element={
            <ProtectedLayout>
              <ListingDetailPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/leasing/leads/:id"
          element={
            <ProtectedLayout>
              <LeadDetailPage />
            </ProtectedLayout>
          }
        />

        {/* Relay simulator (dev-only) routes */}
        <Route
          path="/relay/simulator/:caseId"
          element={
            <ProtectedLayout>
              <RelaySimulatorCasePage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/relay/simulator"
          element={
            <ProtectedLayout>
              <RelaySimulatorPage />
            </ProtectedLayout>
          }
        />
        {/* Agent Review Queue — held agent drafts pending manager approval (BR-QUEUE-AUTHZ) */}
        <Route
          path="/agent/review-queue"
          element={
            <ProtectedLayout>
              <AgentResponseQueuePage />
            </ProtectedLayout>
          }
        />

        {/* Improvement conversation, full page (deep link; the panel normally opens as a sheet) */}
        <Route
          path="/agent-conversations/:conversationId"
          element={
            <ProtectedLayout>
              <ImprovementConversationPage />
            </ProtectedLayout>
          }
        />

        {/* Agent Escalation Queue — open escalations awaiting a landlord answer (BR-AGENT-ESC-1, BR-ESC-AUTHZ) */}
        <Route
          path="/agent/escalations"
          element={
            <ProtectedLayout>
              <AgentEscalationQueuePage />
            </ProtectedLayout>
          }
        />

        {/* Redirect old funding-sources route to payments page */}
        <Route path="/funding-sources" element={<Navigate to="/payments" replace />} />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </HelpProvider>
      </ImprovementConversationProvider>
      </PageTitleProvider>
      </InstallPromptProvider>
      </SupportChatProvider>
      </CentrifugoProvider>
    </AuthProvider>
  );
}

/**
 * Main App Component
 */
function App() {
  return (
    <ThemeProvider>
      <AnalyticsProvider
        apiKey={import.meta.env.VITE_POSTHOG_KEY || ''}
        host={import.meta.env.VITE_POSTHOG_HOST}
        appName="landlord"
        enabled={import.meta.env.PROD}
      >
        <AnalyticsErrorBoundary
          app="landlord"
          surface="app-root"
          fallback={(error, reset) => <LandlordErrorFallback error={error} onReset={reset} />}
        >
          <QueryClientProvider client={queryClient}>
            <BrowserRouter
              basename="/landlord"
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <AppRoutes />
            </BrowserRouter>
          </QueryClientProvider>
        </AnalyticsErrorBoundary>
        <CookieConsentRoot />
      </AnalyticsProvider>
    </ThemeProvider>
  );
}

export default App;
